/**
 * App Plan Check Utility
 *
 * Ported from scio2: optisigns-portal-common/libs/internal/src/lib/scio-graphql-api/device/device.utils.ts
 * Determines whether an app requires a higher plan tier than the user's current plan.
 */
import type { AppMeta } from '@scio3/shared-types';
import { PLAN_TIER_ORDER } from '@scio3/shared-types';

interface PlanCheckResult {
  isLocked: boolean;
  minPlanRequired?: string;
}

/**
 * Checks if an app is locked behind a higher plan tier.
 *
 * @param appMeta - The app definition to check
 * @param currentPlan - company.payment.planFeature as-is (e.g. 'standard', 'proPlus').
 *   Pass it unresolved (undefined/null/'') rather than defaulting it: this function owns the
 *   fail-open rule, so callers can't disagree about what a missing plan means.
 * @param companyFeatures - Company feature flags (for featureKey override)
 * @returns { isLocked, minPlanRequired } — isLocked=true means user needs to upgrade
 */
export function checkAppMetaPlan(
  appMeta: AppMeta,
  currentPlan: string | null | undefined,
  companyFeatures: Record<string, boolean | number> = {},
): PlanCheckResult {
  // isLocked is deliberately ignored here, as in scio1 __checkAppMetaPlan and open-api's
  // device.service __checkAppMetaPlan (the push-to-screens gate). Only the Add App catalog
  // gates on it — see checkAppCatalogLock.
  const params = (appMeta.func?.params ?? {}) as Record<string, unknown>;
  const featureKey = params.featureKey as string | undefined;

  // If app has a featureKey override and user has that feature, it's unlocked
  if (featureKey && companyFeatures[featureKey]) {
    return { isLocked: false };
  }

  if (!appMeta.minPlanRequired) {
    return { isLocked: false };
  }

  // Enterprise check — only enterprise or featureKey override can unlock.
  // Runs before plan resolution, as in scio1: it does not depend on the plan tier.
  if (appMeta.minPlanRequired === 'enterprise' && !companyFeatures.isEnterprise) {
    return { isLocked: true, minPlanRequired: appMeta.minPlanRequired };
  }

  // An unresolved plan skips the tier check, as scio1 and open-api do while planFeature is
  // falsy. That is the only fail-open case: a plan alias outside PLAN_TIER_ORDER ranks at
  // -1 and locks, matching their planFeatures.indexOf comparison.
  if (!currentPlan) {
    return { isLocked: false };
  }

  // Compare plan tiers using ordered list. An unknown minPlanRequired (-1) never locks.
  const requiredIdx = PLAN_TIER_ORDER.indexOf(
    appMeta.minPlanRequired as (typeof PLAN_TIER_ORDER)[number],
  );
  const currentIdx = PLAN_TIER_ORDER.indexOf(currentPlan as (typeof PLAN_TIER_ORDER)[number]);
  if (requiredIdx > currentIdx) {
    return { isLocked: true, minPlanRequired: appMeta.minPlanRequired };
  }

  return { isLocked: false };
}

/**
 * Plan lock for the Add App catalog only (lock badge, open-app upgrade modal,
 * share-link hiding). Mirrors scio1 appManagementComponent.isLocked: the plan check
 * runs only when AppMeta explicitly sets isLocked=true; absent means unlocked.
 */
export function checkAppCatalogLock(
  appMeta: AppMeta,
  currentPlan: string | null | undefined,
  companyFeatures: Record<string, boolean | number> = {},
): PlanCheckResult {
  if (!appMeta.isLocked) {
    return { isLocked: false };
  }
  return checkAppMetaPlan(appMeta, currentPlan, companyFeatures);
}

export interface FlaggedAsset {
  appName: string;
  minPlanRequired: string;
}

/**
 * Batch plan check for a list of items against appMetas.
 * Reuses checkAppMetaPlan per-item to ensure consistent business rules
 * (featureKey overrides, engage checks). Like the server check, it ignores isLocked.
 *
 * @param items - Items with an aliasType resolved to look up in appMetaMap
 * @param appMetaMap - Map of appType → AppMeta from ME_QUERY
 * @param currentPlan - company.payment.planFeature as-is; unresolved is handled by checkAppMetaPlan
 * @param companyFeatures - Company feature flags for featureKey overrides
 * @param getAliasType - Resolver function to get effective app type per item
 */
export function checkItemsPlanPermissions<T>(
  items: T[],
  appMetaMap: Map<string, AppMeta>,
  currentPlan: string | null | undefined,
  companyFeatures: Record<string, boolean | number>,
  getAliasType: (item: T) => string,
): FlaggedAsset[] {
  const flagged: FlaggedAsset[] = [];

  for (const item of items) {
    const aliasType = getAliasType(item);
    if (!aliasType) continue;

    const appMeta = appMetaMap.get(aliasType);
    if (!appMeta) continue;

    // Engage apps: checkAppMetaPlan doesn't handle the engage flag directly,
    // so check it before delegating to the standard plan check.
    if (appMeta.engage && !appMeta.isFree) {
      const params = (appMeta.func?.params ?? {}) as Record<string, unknown>;
      const featureKey = params.featureKey as string | undefined;
      if (featureKey && companyFeatures[featureKey]) continue;
      flagged.push({
        appName: appMeta.text ?? aliasType,
        minPlanRequired: appMeta.minPlanRequired ?? 'engage',
      });
      continue;
    }

    const result = checkAppMetaPlan(appMeta, currentPlan, companyFeatures);
    if (result.isLocked && result.minPlanRequired) {
      flagged.push({
        appName: appMeta.text ?? aliasType,
        minPlanRequired: result.minPlanRequired,
      });
    }
  }

  return flagged;
}

/**
 * Checks if an app is enterprise-only with a feature gate.
 *
 * Matches scio2 _openAppModal Branch 2: fires when ALL conditions are true:
 * - minPlanRequired === 'enterprise'
 * - company does NOT have isEnterprise
 * - params.featureKey is set (the app has a specific feature gate)
 * - company does NOT have that featureKey enabled
 *
 * Enterprise apps WITHOUT featureKey fall through to the generic plan lock
 * (which opens the subscription modal instead of a warning toast).
 *
 * @param appMeta - The app definition to check
 * @param companyFeatures - Company feature flags (must include isEnterprise)
 * @returns true if enterprise-gated with an unmet featureKey
 */
export function isEnterpriseOnly(
  appMeta: AppMeta,
  companyFeatures: Record<string, boolean | number> = {},
): boolean {
  const params = (appMeta.func?.params ?? {}) as Record<string, unknown>;
  const minPlan = (params.minPlanRequired as string | undefined) ?? appMeta.minPlanRequired;

  if (minPlan !== 'enterprise') return false;
  if (companyFeatures.isEnterprise) return false;

  // Branch 2 requires featureKey to be set AND not enabled.
  // Enterprise apps without featureKey go to generic plan lock (Branch 5).
  const featureKey = params.featureKey as string | undefined;
  if (!featureKey) return false;
  if (companyFeatures[featureKey]) return false;

  return true;
}
